import React from 'react';
import { IInterestSignalRule, InterestSignalSource, IStorefrontInterestConfig, IStorefrontPromoConfig, IStorefrontSection, IStorefrontSectionCell } from '../../../../models/Storefront';
import StorefrontTextEditor from '../StorefrontTextEditor';
import MarkdownView from 'react-showdown';
import { ChoiceGroup, DefaultButton, Dropdown, DropdownMenuItemType, IChoiceGroupOption, Icon, IconButton, IDropdownOption, Image, Label, Panel, PanelType, Pivot, PivotItem, Stack, StackItem, TextField, Toggle, TooltipHost, useTheme } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import RichTextField from '../../../Common/RichTextField';
import NumberField from '../../../Inputs/NumberField';
import { useGetAllProductSegmentsQuery, useGetManufacturersQuery, useGetSubCategoriesQuery, useLazyGetProductCategoriesBySegmentQuery } from '../../../../store/endpoints/ProductCategorizationEndpoints';
import { StorefrontSectionBundlesPreview, StorefrontSectionCategoryProductsPreview, StorefrontSectionFeaturedProductsPreview, StorefrontSectionProductsPreview, StorefrontSectionSavedSearchProductPreview } from './StorefrontSectionItemPreview';
import SectionCssRows from './SectionCssRows';
import SectionStyleActions from './SectionStyleActions';
import CssCopyPasteButtons from './CssCopyPaste';
import { isDarkColor } from '../colorUtils';
import RawJsonEditor from '../RawJsonEditor';
import { isContentSectionType, parseRichTextConfig, youTubeId } from './contentSectionConfig';
import { isFractionalWidth } from './sectionWidths';
import { SECTION_WIDTH_LABELS, sectionTypeLabel } from './sectionTypes';
import { BannerContentForm, ContentSectionPreview, DividerContentForm, RichTextContentForm, SingleProductContentForm, VideoContentForm } from './ContentSectionForms';
import SelectedProductsForm from './SelectedProductsForm';
import ImageEditor from '../../../Inputs/ImageEditor';
import ProductTagsAutoSuggest from '../../../Search/ProductTagsAutoSuggest';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import { useGetVendorsQuery } from '../../../../store/Api';
import { useGetOrganizationQuery } from '../../../../store/endpoints/OrganizationsEndpoints';
import { useGetPromotionsQuery } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import SearchFilter from '../../../Common/SearchFilter';
import { elementStyleFrom, StyleGroupHeader, StyleOverridesGroup } from '../styleControls';
import { DynamicSetting } from '../../../Common/Setting';
import ConditionalSetting from '../../../Common/ConditionalSetting';
import { StyleRowsWidthProvider } from '../styleRows';
import {
    resolveElementStyle,
    resolveStyleProps,
    SECTION_OVERRIDE_KEYS,
    SECTION_OVERRIDE_PARTS,
    STYLE_PART_ICONS,
    STYLE_PART_LABELS,
    styleTargetForKey,
    STYLE_REF_KEY,
} from '../stylesConfig';
import { StorefrontCardChromeProvider, useCardChrome } from '../StorefrontCardChromeContext';
import { useStorefrontStyles } from '../StorefrontStylesContext';

interface IStorefrontSectionEditorProps {
    section: IStorefrontSection;
    onChange: (section: IStorefrontSection) => void;
    editMode?: boolean;
    storefrontId?: string;
    onDelete?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onApplyToAll?: () => void;
    // The storefront's raw homePageConfig JSON. Only used when saving this section's appearance as a
    // style: the capture stamps the page-level styling that affects the section (AC-1165), and without
    // it a saved style would carry the container and nothing painting inside it.
    homePageConfig?: string;
    isDark?: boolean;
    // When provided (edit mode), the previewed product cards are clickable and open the sections
    // editor's quick style popover, anchored on the clicked card (passed back as the argument).
    onEditCardStyle?: (target: HTMLElement) => void;
    // Deep link from that popover's "Advanced overrides": open this panel straight onto the
    // Appearance tab with the Product Cards group expanded and scrolled to. One-shot — the parent
    // clears it via onInitialFocusHandled as soon as it's consumed, so clicking again re-triggers it.
    // 'basic' arrives from a settings deep link into the storefront.sectionField.* family (Section
    // Type, Width, the per-type content forms, ...) — those live on the Basic tab, not Appearance.
    initialFocus?: 'productStyle' | 'appearance' | 'basic';
    onInitialFocusHandled?: () => void;
    // Glyphs for the collapse/expand indicator (homePageConfig). Absent falls back to the shipped
    // '+' / '−', matching the storefront.
    sectionToggleIcons?: { expand: string; collapse: string };
    // Shopper-facing label for the per-section "More like this" link, already run through the
    // storefront's localization overrides by the parent. Absent = don't render the link at all.
    moreLikeThisLabel?: string;
}

// Pivot tab keys. Stable ids (rather than Fluent's positional defaults) because the Appearance tab
// is deep-linkable — see initialFocus.
type SectionTabKey = 'basic' | 'header' | 'footer' | 'appearance' | 'advanced';

// "Clear style": drop every local override but keep the $styleRef, so the element falls back to
// the named style's values instead of losing its style assignment along with the tweaks.
const clearedStyleProps = (css?: Record<string, unknown>): Record<string, unknown> => {
    const ref = css?.[STYLE_REF_KEY];
    return typeof ref === 'string' && ref ? { [STYLE_REF_KEY]: ref } : {};
};

// The signals an MSP can feed into a "Recommended For You" (interest) section. The section starts
// with none — the MSP explicitly adds the ones they want. defaultWeight matches the BFF's per-source
// default and is shown as the placeholder when no per-rule weight is set. cartRemoved is negative.
const INTEREST_SOURCES: { key: InterestSignalSource; label: string; defaultWeight: number; hint: string }[] = [
    { key: 'favorited', label: 'Favorited', defaultWeight: 3, hint: 'Products the shopper has favorited.' },
    { key: 'purchased', label: 'Purchased', defaultWeight: 2, hint: 'Products the shopper bought before (reorder intent).' },
    { key: 'viewed', label: 'Viewed', defaultWeight: 1, hint: 'Products the shopper viewed but did not buy.' },
    { key: 'viewedNotCarted', label: 'Viewed, not added to cart', defaultWeight: 2, hint: 'Products the shopper drilled into (opened the detail page) but never added to their cart — considered but did not convert.' },
    { key: 'cartAdded', label: 'Added to cart', defaultWeight: 4, hint: 'Products added to the cart (added, not yet purchased or removed) — strong intent.' },
    { key: 'cartRemoved', label: 'Removed from cart (negative)', defaultWeight: 4, hint: 'Demotes products the shopper added and then removed from their cart.' },
    { key: 'othersPurchased', label: 'Others bought (storefront-wide)', defaultWeight: 1, hint: 'What other shoppers of this storefront have bought.' },
    { key: 'similarToRecentlyViewed', label: 'Similar to recently viewed', defaultWeight: 1, hint: 'New-to-the-shopper products that resemble ones they looked at recently (same category, brand, tags). Pulls in items they have not seen yet.' },
];

const mergeStyles = (base?: React.CSSProperties, override?: React.CSSProperties): React.CSSProperties => {
    const filtered = Object.fromEntries(
        Object.entries(override ?? {}).filter(([, v]) => v !== undefined && v !== '')
    );
    return { ...(base ?? {}), ...filtered };
};

// MIRROR of the storefront renderer's Components/Helpers/sectionTypography.ts. A section title used
// to carry no typography of its own, so it rendered SMALLER than a `##` heading authored inside the
// same section's body. The storefront now gives every section title this default scale, spread UNDER
// the merchant's titleStyleProps so anything they set still wins. This preview is the spec operators
// design against, so it must carry the identical numbers. Change them in both places or not at all.
const SECTION_TITLE_DEFAULTS: React.CSSProperties = {
    fontSize: '1.6em',
    fontWeight: 600,
    lineHeight: 1.25,
};

// MIRROR of the storefront's Components/Helpers/sectionTypography.ts (cell title scale, cell media
// sizing) and Components/Sections/SectionCells.tsx (media-first order). This preview is the surface
// operators design against, so it must render a cell the way the storefront renders it. Change these
// rules in BOTH places or in neither.
//
// A cell's `title` serves two shapes and they do not want the same scale:
//   * a STAT FIGURE ("2,000+", "90%", "24/7") over a caption. The figure is the effect, so it is
//     large.
//   * a CARD HEADING ("Managed Device Services") over a paragraph. At the stat scale it wraps to two
//     lines and renders larger than the section title above it, inverting the page hierarchy.
// Size is relative to the CELL either way, so the cell's own styleProps.fontSize scales the pair;
// colour and alignment are inherited from the cell. To style the title alone, the cell's styleProps
// carry a nested dictionary under the reserved `$title` key, which overrides either scale completely
// and is stripped from the cell's own style so it never reaches a style attribute.
const CELL_TITLE_STYLE_KEY = '$title';
// Short and numeric = a figure. Words that merely mention a number run past the cap and are read as a
// heading, which is the safe direction to be wrong in.
const CELL_STAT_TITLE_MAX_LENGTH = 12;
const CELL_STAT_TITLE_DEFAULTS: React.CSSProperties = {
    fontSize: '2.25em',
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: '0.15em',
};
const CELL_HEADING_TITLE_DEFAULTS: React.CSSProperties = {
    fontSize: '1.2em',
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: '0.35em',
};

// A card image is an icon or a supporting illustration: it keeps its proportions, never exceeds the
// column, and is capped so it cannot dominate the card. A media-only cell (no title, no text) is a
// deliberate full-bleed image and keeps the legacy force-fill style.
const CARD_MEDIA_MAX_HEIGHT = '120px';
const CELL_MEDIA_SIZING_KEYS = ['width', 'minWidth', 'maxWidth', 'height', 'minHeight', 'maxHeight'];

const isStatTitle = (title?: string): boolean => {
    const trimmed = (title ?? '').trim();
    if (trimmed.length === 0 || trimmed.length > CELL_STAT_TITLE_MAX_LENGTH) return false;
    return /\d/.test(trimmed);
};

const cellBodyStyle = (styleProps?: { [key: string]: unknown }): React.CSSProperties => {
    if (!styleProps || !(CELL_TITLE_STYLE_KEY in styleProps)) return (styleProps ?? {}) as React.CSSProperties;
    const copy = { ...styleProps };
    delete copy[CELL_TITLE_STYLE_KEY];
    return copy as React.CSSProperties;
};

const cellTitleStyle = (styleProps?: { [key: string]: unknown }, title?: string): React.CSSProperties => {
    const base = isStatTitle(title) ? CELL_STAT_TITLE_DEFAULTS : CELL_HEADING_TITLE_DEFAULTS;
    const override = styleProps?.[CELL_TITLE_STYLE_KEY];
    if (!override || typeof override !== 'object' || Array.isArray(override)) return base;
    return { ...base, ...(override as React.CSSProperties) };
};

const cellMediaStyle = (bodyStyle: React.CSSProperties, isCard: boolean): React.CSSProperties => {
    if (!isCard) {
        return { ...bodyStyle, width: '100%', minWidth: 'unset', maxWidth: 'unset', minHeight: 'unset', height: '100%', maxHeight: 'unset' };
    }
    // Only the merchant's own SIZING carries onto the image; the rest of a card's styleProps
    // (padding, background, border, alignment) belong to the card and stay on the cell.
    const sizing: { [key: string]: unknown } = {};
    for (const key of CELL_MEDIA_SIZING_KEYS) {
        const value = (bodyStyle as { [key: string]: unknown })[key];
        if (value !== undefined && value !== null && value !== '') sizing[key] = value;
    }
    return { maxWidth: '100%', height: 'auto', objectFit: 'contain', maxHeight: CARD_MEDIA_MAX_HEIGHT, ...sizing };
};

const StorefrontSectionEditor: React.FC<IStorefrontSectionEditorProps> = (props) => {
    const [calloutOpen, setCalloutOpen] = React.useState(false);
    const theme = useTheme();
    const editButtonId = useId('callout-button');
    const segments = useGetAllProductSegmentsQuery()
    const [categories] = useLazyGetProductCategoriesBySegmentQuery()
    const manufacturers = useGetManufacturersQuery()
    const subCategories = useGetSubCategoriesQuery({ categories: props.section.categories ?? [] })
    const [tempHeaderCells, setTempHeaderCells] = React.useState(props.section.headerCells ?? []);
    const [tempFooterCells, setTempFooterCells] = React.useState(props.section.footerCells ?? []);
    const [headerCellCount, setHeaderCellCount] = React.useState(props.section.headerCells?.length ?? 0);
    const [footerCellCount, setFooterCellCount] = React.useState(props.section.footerCells?.length ?? 0);
    const [categoryOptions, setCategoryOptions] = React.useState<IDropdownOption[] | undefined>();
    const [loadingCategories, setLoadingCategories] = React.useState(false);
    // Appearance tab: the two style groups collapse independently (both may be open at once).
    // The Pivot is controlled so the card popover can deep-link into the Appearance tab; opening the
    // panel normally always starts on Basic.
    const [selectedTab, setSelectedTab] = React.useState<SectionTabKey>('basic');
    // Set only by the deep link: expands the Product Cards overrides and scrolls it into view.
    const [focusProductStyle, setFocusProductStyle] = React.useState(false);
    const productGroupRef = React.useRef<HTMLDivElement>(null);

    // Grouped the same way as the Add Section menu: product types, then the content types.
    // Texts come from SECTION_TYPE_LABELS so this dropdown and the Sections picker call the same
    // type by the same name; the order and the content divider are this list's own.
    const typeOption = (key: string): IDropdownOption => ({ key, text: sectionTypeLabel(key) });
    const typeDropdownOptions: IDropdownOption[] = [
        typeOption('featured'),
        typeOption('bundles'),
        typeOption('dynamicProductListing'),
        typeOption('savedSearches'),
        typeOption('selectedProducts'),
        typeOption('categoriesSegments'),
        typeOption('interest'),
        typeOption('promos'),
        typeOption('selectedPromos'),
        // singleProduct lists with the product types (it displays a product), even though the
        // editor internally gates it like a content type (contentConfig form, no maxEntries).
        typeOption('singleProduct'),
        { key: 'contentDivider', text: '-', itemType: DropdownMenuItemType.Divider },
        { key: 'contentHeader', text: 'Content Sections', itemType: DropdownMenuItemType.Header },
        typeOption('video'),
        typeOption('richText'),
        typeOption('banner'),
        typeOption('divider'),
    ]

    // Width applies to every section type; absent = 'full' (the historical single-column default).
    // Widest first, so the list reads as a shrinking column.
    const widthOptions: IDropdownOption[] = Object.entries(SECTION_WIDTH_LABELS).map(([key, text]) => ({ key, text }));

    // Only meaningful for fractional sections sharing a row; 'Stretch' (absent) is the historical default.
    const verticalAlignOptions: IDropdownOption[] = [
        { key: 'stretch', text: 'Stretch (default)' },
        { key: 'top', text: 'Top' },
        { key: 'center', text: 'Center' },
        { key: 'bottom', text: 'Bottom' },
    ]

    // Content sections (video/richText/banner) have no products — hide the product
    // query fields, max-entries, side-scroll, and header/footer cell layout, and show a per-type
    // content form instead.
    const isContent = isContentSectionType(props.section.type);

    const cellTypeOptions: IDropdownOption[] = [
        { key: 'text', text: 'Text' },
        { key: 'image', text: 'Image' },
        { key: 'video', text: 'Video' },
    ]

    const interestCfg = props.section.interestConfig ?? {};
    const updateInterest = (patch: Partial<IStorefrontInterestConfig>) =>
        props.onChange({ ...props.section, interestConfig: { ...interestCfg, ...patch } });

    const interestSignals: IInterestSignalRule[] = interestCfg.signals ?? [];
    const addInterestSignal = (source: InterestSignalSource) =>
        updateInterest({ signals: [...interestSignals, { source }] });
    const removeInterestSignal = (source: InterestSignalSource) =>
        updateInterest({ signals: interestSignals.filter(s => s.source !== source) });
    const setInterestSignalWeight = (source: InterestSignalSource, weight?: number) =>
        updateInterest({ signals: interestSignals.map(s => s.source === source ? { ...s, weight } : s) });
    // Sources not yet added — offered in the "Add a signal" dropdown (one rule per source).
    const availableInterestSources = INTEREST_SOURCES.filter(s => !interestSignals.some(x => x.source === s.key));

    // Curated promo section. Both lists are opt-in: an empty configuration shows nothing rather than
    // everything, so a half-configured row is empty instead of accidentally advertising the whole catalogue.
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organizationForPromos = useGetOrganizationQuery(selectedOrganization ?? '', { skip: !selectedOrganization });
    const promoCatalog = organizationForPromos.data?.defaultCatalog;
    const isSelectedPromos = props.section.type === 'selectedPromos';
    // Only fetched for the section type that uses them, so every other section costs nothing.
    const promotions = useGetPromotionsQuery(
        { catalog: promoCatalog ?? '', storefront: props.storefrontId, includeInactive: false },
        { skip: !promoCatalog || !isSelectedPromos });
    const promoVendorList = useGetVendorsQuery(promoCatalog ?? '', { skip: !promoCatalog || !isSelectedPromos });

    const promoCfg = props.section.promoConfig ?? {};
    const updatePromo = (patch: Partial<IStorefrontPromoConfig>) => {
        props.onChange({ ...props.section, promoConfig: { ...promoCfg, ...patch } });
    };
    const selectedPromotionIds: string[] = promoCfg.promotionIds ?? [];
    const selectedPromoVendors: string[] = promoCfg.vendors ?? [];

    const onClick = () => {
        if (props.editMode) {
            setSelectedTab('basic');
            setFocusProductStyle(false);
            setCalloutOpen(true);
        }
    }

    const loadCategories = async (segments?: string[]) => {
        if (segments == null || segments.length == 0) {
            setCategoryOptions(undefined);
            return;
        }
        setLoadingCategories(true);
        const cats: IDropdownOption[] = [];
        for (const segment of segments) {
            const results = await categories(segment);
            results.data?.items.forEach((cat) => {
                if (cat.label)
                cats.push({ key: cat.label, text: `${segment} - ${cat.label}` });
            })
        }
        setCategoryOptions(cats);
        setLoadingCategories(false);
    }

    // Storefront Styles: a section's five StyleProps dictionaries may each carry a `$styleRef`
    // naming a reusable style. Resolve here (preset underneath, the section's own props on top,
    // `$styleRef` stripped) so every preview spread below sees plain CSS.
    const { styles: availableStyles } = useStorefrontStyles();
    const styleProps = resolveStyleProps(props.section.styleProps, 'styleProps', availableStyles);
    const titleStyleProps = resolveStyleProps(props.section.titleStyleProps, 'titleStyleProps', availableStyles);
    const headerStyleProps = resolveStyleProps(props.section.headerStyleProps, 'headerStyleProps', availableStyles);
    const footerStyleProps = resolveStyleProps(props.section.footerStyleProps, 'footerStyleProps', availableStyles);

    // The section's overrides for the page-level layers (AC-1165). Resolved on their own, not merged with
    // the page values: those already reach this preview through the scoped stylesheet, and an inline style
    // (or a nested chrome provider) layers over it exactly as the shopper's cascade does.
    const overrideStyle = (key: string): React.CSSProperties | undefined =>
        resolveElementStyle(props.section.styleOverrides?.[key], SECTION_OVERRIDE_PARTS[key], availableStyles) as
            React.CSSProperties | undefined;
    const titleTextOverride = overrideStyle('sectionTitleText');
    // Inherited page chrome with this section's overrides on top. A nested provider is all it takes: the
    // add-to-cart button and the scroll controls read the nearest one (useCardChrome), so the preview of
    // THIS section gets its own button and scroll styling without any prop threading.
    const pageChrome = useCardChrome();
    const sectionChrome = {
        ...pageChrome,
        addToCartStyle: { ...pageChrome.addToCartStyle, ...overrideStyle('addToCartButton') },
        scrollButtonStyle: { ...pageChrome.scrollButtonStyle, ...overrideStyle('scrollButton') },
        scrollbarStyle: { ...pageChrome.scrollbarStyle, ...overrideStyle('scrollbar') },
    };

    // Rich text CTA (contentConfig.button). Renders ONLY when a label is set, above or below the
    // content columns per `position` (default 'end'), aligned per `align` (default 'left'). The
    // default look mirrors the shopper's banner CTA — a blue pill with white text — and the
    // button's own style (which may carry a `$styleRef` to a named button style) layers on top.
    const richTextButtonConfig = props.section.type === 'richText'
        ? parseRichTextConfig(props.section.contentConfig).button
        : undefined;
    const richTextButtonLabel = richTextButtonConfig?.label?.trim();
    const richTextButton = richTextButtonLabel ? (
        <div style={{
            display: 'flex',
            justifyContent: richTextButtonConfig?.align === 'center' ? 'center'
                : richTextButtonConfig?.align === 'right' ? 'flex-end'
                : 'flex-start',
            padding: '0.5em 0',
        }}>
            <a
                href={richTextButtonConfig?.url || '#'}
                // Preview only — never navigate out of the editor.
                onClick={(e) => e.preventDefault()}
                style={{
                    display: 'inline-block',
                    backgroundColor: '#0f6cbd',
                    color: '#fff',
                    padding: '12px 28px',
                    borderRadius: 4,
                    textDecoration: 'none',
                    fontWeight: 600,
                    ...elementStyleFrom(resolveStyleProps(richTextButtonConfig?.style, 'buttonStyle', availableStyles)),
                }}
            >
                {richTextButtonLabel}
            </a>
        </div>
    ) : null;

    // Layout behaviour, not a style override, so it sits under the picker and never folds away.
    const fullWidthBackgroundToggle = (
        <div style={{ marginTop: '0.75em' }}>
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.25em' }}>
                <DynamicSetting id={`storefront.sectionField.${props.section.id}.fullBleed`}>
                    <Toggle
                        styles={{ root: { marginBottom: 0 } }}
                        label='Extend background edge-to-edge'
                        inlineLabel
                        onText='Full width'
                        offText='Contained'
                        checked={props.section.fullWidthBackground ?? false}
                        onChange={(_, checked) => props.onChange({ ...props.section, fullWidthBackground: checked ?? false })}
                    />
                </DynamicSetting>
                <TooltipHost content="The background color spans the full viewport width. When side-by-side sections share a row, the color bands the whole row (using the first such section's background color).">
                    <Icon iconName='Info' style={{ color: '#605e5c', cursor: 'help' }} />
                </TooltipHost>
            </Stack>
            <div style={{ fontSize: 12, color: '#605e5c', marginTop: '0.5em' }}>
                Bands the full row edge-to-edge when side-by-side sections share a row.
            </div>
        </div>
    );

    // styleProps is merchant-authored JSON, so backgroundColor is not guaranteed to be a
    // readable color string. Anything unparseable falls back to the inherited page darkness.
    const sectionBackgroundColor = styleProps?.backgroundColor ?? headerStyleProps?.backgroundColor;
    const sectionIsDark = isDarkColor(sectionBackgroundColor, props.isDark ?? false);
    // Contrast color for anything drawn ON the header band — the storefront's own headerControlColor
    // (HomePage.tsx), same source order, same luminance rule. The band's text color is NOT usable for
    // this: a merchant may legitimately set it to the band color itself and rely on a knocked-out
    // title block (homePageConfig.sectionTitleText) to give the TITLE its contrast, which leaves
    // everything else on the band — the collapse glyph, the sub-header placeholder — invisible.
    const headerBandColor = titleStyleProps?.backgroundColor ?? headerStyleProps?.backgroundColor ?? styleProps?.backgroundColor;
    const headerControlColor = headerBandColor == null
        ? 'inherit'
        : (isDarkColor(headerBandColor, props.isDark ?? false) ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)');

    React.useEffect(() => {
        loadCategories(props.section.segments);
    }, [props.section.segments])

    // Deep link from the card popover: open onto Appearance with the Product Cards group focused.
    // Consumed immediately (onInitialFocusHandled) so the parent's one-shot state resets and a
    // second click re-fires this. Content sections have no Product Cards group at all — they still
    // land on Appearance, just without the focus, rather than pointing at nothing.
    //
    // 'basic' is the odd one out: a storefront.sectionField.* deep link (Section Type, Width, the
    // per-type content forms) targets a Basic-tab control, not Appearance — sending it to Appearance
    // would land two tabs away from the field it names.
    React.useEffect(() => {
        if (!props.initialFocus) return;
        setSelectedTab(props.initialFocus === 'basic' ? 'basic' : 'appearance');
        // 'appearance' arrives from the Sections picker, which asked for the tab and nothing more,
        // or from a settings deep link, which names its own style group. Neither wants the Product
        // Cards fold forced open on top of what it asked for.
        setFocusProductStyle(props.initialFocus === 'productStyle' && !isContent);
        setCalloutOpen(true);
        props.onInitialFocusHandled?.();
    }, [props.initialFocus]);

    // The Panel mounts into a Layer and slides in; scrolling before that settles is a no-op, so wait
    // out the animation rather than racing it.
    React.useEffect(() => {
        if (!calloutOpen || !focusProductStyle) return;
        const timer = window.setTimeout(() => {
            productGroupRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 250);
        return () => window.clearTimeout(timer);
    }, [calloutOpen, focusProductStyle]);



    React.useEffect(() => {
        if (props.section.headerCells) {
            setHeaderCellCount(props.section.headerCells.length);
            setTempHeaderCells(props.section.headerCells);
        }
        if (props.section.footerCells) {
            setFooterCellCount(props.section.footerCells.length);
            setTempFooterCells(props.section.footerCells);
        }
    }, [props.section.headerCells, props.section.footerCells]);

    const changeHeaderCellCount = (ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, option?: IChoiceGroupOption) => {
        const newCount = Number(option?.key);
        setHeaderCellCount(newCount);
        const newCells: IStorefrontSectionCell[] = [];
        for(let i = 0; i < newCount; i++) {
            if (props.section.headerCells && props.section.headerCells[i]) {
                newCells.push(props.section.headerCells[i]);
            } else {
                newCells.push({ type: 'text', text: '' });
            }
        }
        setTempHeaderCells(newCells);
    }

    const closeCallout = () => {
        setCalloutOpen(false);
        props.onChange({ ...props.section, headerCells: tempHeaderCells, footerCells: tempFooterCells });
    }

    // Rich text sections store their content as 1–3 typed column cells in headerCells (the same
    // mechanism as product-section header/footer cells — same temp-state, committed on panel close).
    const changeRichTextColumnCount = (_?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
        const newCount = Number(option?.key);
        const newCells: IStorefrontSectionCell[] = [];
        for (let i = 0; i < newCount; i++) {
            newCells.push(tempHeaderCells[i] ?? { type: 'text', text: '' });
        }
        setTempHeaderCells(newCells);
    };
    const moveRichTextCell = (index: number, dir: -1 | 1) => {
        const target = index + dir;
        if (target < 0 || target >= tempHeaderCells.length) return;
        const next = [...tempHeaderCells];
        [next[index], next[target]] = [next[target], next[index]];
        setTempHeaderCells(next);
    };

    // Nested so the add-to-cart button and the scroll controls inside THIS section read its own
    // chrome (useCardChrome takes the nearest provider) rather than the page-wide one. AC-1165.
    return <StorefrontCardChromeProvider {...sectionChrome}>
        <div>
        <div
            style={{
                // Edit-mode scaffolding so an unstyled section is still a visible, clickable box. DASHED,
                // matching the placeholder states below, because a solid 1px grey frame is indistinguishable
                // from a real one: a section whose own border is a thin grey line looks identical with and
                // without it, and a section whose styling has just been REMOVED looks like it kept its border.
                //
                // OUTLINE, not border, and the distinction is load-bearing. React diffs inline styles between
                // renders, so toggling edit mode ADDS or REMOVES the `border` shorthand — and writing (or
                // clearing) a shorthand resets every border longhand on the element. A section styled with
                // longhands (borderTopColor, borderBottomWidth, …) has those unchanged across the toggle, so
                // React never re-applies them and they are simply gone until the next mount. That is what made
                // a storefront's gold/maroon section edges vanish on entering edit mode, stay gone on exiting
                // it, and come back on refresh. Spreading styleProps afterwards does not save it: that only
                // orders the FIRST paint, and this is a diff against the paint before.
                //
                // An outline cannot collide with any border property, and it does not take part in layout
                // either, so entering edit mode no longer nudges every section by the width of a border.
                // Inset by a pixel so it draws inside the box rather than over whatever sits above.
                ...(props.editMode ? { outline: '1px dashed #ccc', outlineOffset: '-1px' } : {}),
                padding: '1em',
                marginBottom: '1em',
                ...mergeStyles(headerStyleProps as React.CSSProperties, styleProps as React.CSSProperties),
                position: 'relative'
            }}
            >
            {/* Move / edit controls. The class carries the stacking order and the chip behind the
                icons: the header band below is a POSITIONED sibling that comes later in the tree, so
                without a z-index of its own this cluster paints underneath the band — an opaque band
                color hides the section's only inline editing affordance completely. The chip also
                keeps the icons legible on whatever color the band underneath is. */}
            {props.editMode && <div className='storefront-section-controls'>
                {props.onMoveUp && !calloutOpen && <IconButton
                    iconProps={{ iconName: 'ChevronUp' }}
                    title='Move Section Up'
                    ariaLabel='Move Section Up'
                    onClick={props.onMoveUp}
                    />
                }
                {props.onMoveDown && !calloutOpen && <IconButton
                    iconProps={{ iconName: 'ChevronDown' }}
                    title='Move Section Down'
                    ariaLabel='Move Section Down'
                    onClick={props.onMoveDown}
                    />
                }
                <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    title='Edit Section'
                    ariaLabel='Edit Section'
                    onClick={onClick}
                    id={editButtonId}
                />
            </div>}
            {/* Title renders as the storefront does, so the editor preview matches the live page.
                When hideTitle is set the shopper drops the header entirely; the editor still shows
                it, dimmed, so the section stays identifiable while editing.

                Two boxes, mirroring the storefront's own section header: an outer one that is
                always the full width of the section, and the title BAND inside it carrying
                titleStyleProps. They were a single element until a template whose band is
                `width: fit-content` showed why they cannot be — see the glyph below. Still an
                <h3> because the page's scoped stylesheet reaches the glyph through `h3:hover`. */}
            <h3 style={{
                margin: 0,
                position: 'relative',
                // Neutralized: an <h3>'s own UA font-size would otherwise become the base that the
                // band's `1.6em` resolves against, shrinking every section title.
                fontSize: 'inherit',
                fontWeight: 'inherit',
                ...(props.section.hideTitle ? { opacity: 0.45 } : {}),
            }}>
                {/* Collapse/expand indicator, drawn exactly where the storefront draws it: the same
                    absolute inset, measured from the full-width box above rather than from the band,
                    which is why it needs that box. The preview always shows the EXPANDED glyph:
                    every section renders open in the editor, so showing '+' would claim a collapsed
                    state the merchant isn't looking at.
                    Its styling comes from the page's scoped stylesheet (.storefront-section-toggle in
                    StorefrontHomePageEditor), so a merchant's sectionToggle config lands here without
                    this component having to know about it. Purely decorative: the preview header is
                    an editing surface, not a shopper toggle. */}
                {!props.section.hideTitle && props.section.collapsible !== false && (
                    <span
                        aria-hidden='true'
                        className='storefront-section-toggle'
                        style={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            right: 20,
                            width: 24,
                            height: 24,
                            // Luminance-aware against the band, exactly as the storefront draws it.
                            // Inheriting the band's text color instead is what made this invisible on
                            // a store whose title color IS its band color.
                            color: headerControlColor,
                            opacity: 0.65,
                            fontSize: 20,
                            lineHeight: 1,
                            fontWeight: 'normal',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        {props.sectionToggleIcons?.collapse ?? '−'}
                    </span>
                )}
                {/* The title band. titleStyleProps goes HERE and not on the box above, so a band the
                    template sizes to its own text (Meridian's underline treatment is
                    `width: fit-content`) shrinks the band alone and leaves the glyph at the
                    section's edge, which is what the storefront does. */}
                <div style={mergeStyles(SECTION_TITLE_DEFAULTS, titleStyleProps as React.CSSProperties)}>
                    {/* Title text in its own inner div, sub-header below it — both inside the band, so
                        its background and padding wrap both, matching the shopper render (the
                        hideTitle dim sits above them and covers both). The text carries the same
                        class the storefront gives it, so a merchant's sectionTitleText styling — the
                        knocked-out block — previews here too. */}
                    {/* The page's own sectionTitleText arrives through the scoped stylesheet; this is the
                        section's override on top of it (AC-1165). */}
                    <div className='storefront-section-title-text' style={titleTextOverride}>
                        <StorefrontTextEditor
                            value={props.section.title}
                            notSetValue={'Section Title'}
                            onChange={(title) => props.onChange({ ...props.section, title })}
                            editable={props.editMode}
                        />
                        {props.section.hideTitle && (
                            <span style={{ marginLeft: '0.5em', fontSize: 12, fontWeight: 400, whiteSpace: 'nowrap', verticalAlign: 'middle', opacity: 0.8 }}>
                                <Icon iconName='Hide3' style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                (hidden on storefront)
                            </span>
                        )}
                    </div>
                    {/* A SET sub-header renders in the band's own text color, faithfully — if that reads
                        badly the live page reads the same way and the merchant needs to know. The
                        PLACEHOLDER is editor chrome, so it takes the band-contrast color instead of
                        inheriting a color that may match the band. */}
                    <div style={{ fontSize: '0.65em', opacity: 0.85, fontWeight: 'normal' }}>
                        <StorefrontTextEditor
                            value={props.section.subtitle}
                            placeholderStyle={{ color: headerControlColor }}
                            notSetValue={'Add sub-header…'}
                            onChange={(subtitle) => props.onChange({ ...props.section, subtitle })}
                            editable={props.editMode}
                        />
                    </div>
                </div>
            </h3>
            {/* <div className='storefront-section-cell-container'> */}
            {richTextButtonConfig?.position === 'start' && richTextButton}
            <Stack horizontal wrap tokens={{childrenGap: '1em'}}>
                {tempHeaderCells?.map((cell, index) => (
                    <StorefrontSectionCellEditor
                        cell={cell}
                        onChange={(updatedCell) => {
                            const updatedCells = [...(props.section?.headerCells ?? [])];
                            updatedCells[index] = updatedCell;
                            props.onChange({ ...props.section, headerCells: updatedCells });
                        }}
                        editMode={props.editMode}
                        key={index}
                    />
                ))}
            </Stack>
            {richTextButtonConfig?.position !== 'start' && richTextButton}
            {isContent && <ContentSectionPreview section={props.section} isDark={sectionIsDark} />}
            {props.section.type === 'featured' && <StorefrontSectionFeaturedProductsPreview section={props.section} storefrontId={props.storefrontId} isDark={sectionIsDark} onEditCardStyle={props.onEditCardStyle} />}
            {props.section.type === 'bundles' && <StorefrontSectionBundlesPreview section={props.section} storefrontId={props.storefrontId} isDark={sectionIsDark} onEditCardStyle={props.onEditCardStyle} />}
            {props.section.type === 'categoriesSegments' && <StorefrontSectionCategoryProductsPreview section={props.section} storefrontId={props.storefrontId} segments={props.section.segments} categories={props.section.categories} manufacturers={props.section.manufacturers} subCategories={props.section.subCategories} tags={props.section.tags} isDark={sectionIsDark} onEditCardStyle={props.onEditCardStyle} />}
            {props.section.type === 'savedSearches' && <StorefrontSectionSavedSearchProductPreview section={props.section} storefrontId={props.storefrontId} isDark={sectionIsDark} onEditCardStyle={props.onEditCardStyle} />}
            {props.section.type === 'selectedProducts' && <StorefrontSectionProductsPreview section={props.section} storefrontId={props.storefrontId} isDark={sectionIsDark} onEditCardStyle={props.onEditCardStyle} />}
            {props.section.type === 'interest' && (
                <div style={{ border: '1px dashed #b7b7b7', borderRadius: 6, padding: '1.25em', textAlign: 'center', color: sectionIsDark ? '#ddd' : '#605e5c', fontSize: 13 }}>
                    Personalized per signed-in shopper — the products shown here are chosen live from each
                    shopper&rsquo;s favorites, views, and purchases, so there is no fixed preview.
                </div>
            )}
            {props.section.type === 'selectedPromos' && (
                <div style={{ border: '1px dashed #b7b7b7', borderRadius: 6, padding: '1.25em', textAlign: 'center', color: sectionIsDark ? '#ddd' : '#605e5c', fontSize: 13 }}>
                    {selectedPromotionIds.length === 0 && selectedPromoVendors.length === 0
                        ? 'Nothing selected yet — this section will not appear until you choose at least one promotion or distributor.'
                        : 'Resolved live from the promotions running right now, so this row empties itself when they end.'}
                </div>
            )}
            {/* "More like this" — the link the storefront renders under a section whose filters can
                build a search URL (HomePage.tsx: category / segment / manufacturer / subcategory,
                first value of each). Gated on exactly that condition, so the preview shows the link
                for precisely the sections the shopper gets one on, and nothing for a hand-picked
                section that could never link anywhere. Styling arrives via the scoped
                .storefront-more-like-this rule (homePageConfig.moreLikeThisButton); the base look
                below mirrors the shipped .more-like-this-button so an unstyled store previews true.
                Not a link element: there is nowhere to navigate to in an editor. */}
            {props.moreLikeThisLabel && [
                props.section.categories, props.section.segments,
                props.section.manufacturers, props.section.subCategories,
            ].some((f) => !!f?.length) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: 20, paddingBottom: 4 }}>
                    <span
                        className='storefront-more-like-this'
                        style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: '0.85em', opacity: 0.6, padding: '4px 0' }}
                    >
                        {props.moreLikeThisLabel}
                    </span>
                </div>
            )}
            {/* Footer band LAST, after the more-like-this link — matching HomePage.tsx. A styled
                footer band is the section's closing rule, so a link below it reads as belonging to
                the next section. */}
            <Stack horizontal wrap tokens={{childrenGap: '1em', padding: '1em 0'}} style={footerStyleProps as React.CSSProperties}>
                {tempFooterCells?.map((cell, index) => (
                    <StorefrontSectionCellEditor
                        cell={cell}
                        onChange={(updatedCell) => {
                            const updatedCells = [...(props.section?.footerCells ?? [])];
                            updatedCells[index] = updatedCell;
                            props.onChange({ ...props.section, footerCells: updatedCells });
                        }}
                        editMode={props.editMode}
                        key={index}
                    />
                ))}
            </Stack>
        </div>
        <Panel
            isOpen={calloutOpen}
            onDismiss={closeCallout}
            headerText='Section Settings'
            type={PanelType.custom}
            customWidth='460px'
            isBlocking={false}
            isFooterAtBottom
            // The sticky footer is transparent by default, so scrolled content shows through
            // behind the buttons — give it an opaque surface and a divider.
            styles={{ footer: { backgroundColor: theme.palette.white, borderTop: `1px solid ${theme.palette.neutralLight}` } }}
            onRenderFooterContent={() => (
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    <Stack horizontal tokens={{ childrenGap: '1em' }}>
                        {props.onDelete && <StackItem grow>
                            <DefaultButton
                                className='btn-warn'
                                style={{ width: '100%' }}
                                text='Delete'
                                onClick={() => { props.onDelete?.(); setCalloutOpen(false); }}
                            />
                        </StackItem>}
                        <StackItem grow>
                            <DefaultButton
                                style={{ width: '100%' }}
                                text={props.section.disabled ? 'Enable' : 'Disable'}
                                onClick={() => { props.onChange({ ...props.section, disabled: !props.section.disabled }); }}
                            />
                        </StackItem>
                    </Stack>
                    {props.onApplyToAll && <DefaultButton
                        style={{ width: '100%' }}
                        text='Apply Appearance to All Sections'
                        iconProps={{ iconName: 'BrushSize' }}
                        onClick={() => { props.onApplyToAll?.(); setCalloutOpen(false); }}
                    />}
                    {/* Renders nothing outside the pages editor: it needs the styles context, and its two
                        commands each hide themselves when there is nothing to save or nothing to apply. */}
                    <SectionStyleActions
                        section={props.section}
                        onChange={props.onChange}
                        homePageConfig={props.homePageConfig}
                        onDone={() => setCalloutOpen(false)}
                    />
                </Stack>
            )}
        >
            {/* Controlled so the card popover can deep-link to Appearance. Header/Footer don't exist
                for content sections, so a section switched to a content type while sitting on one of
                them falls back to Basic rather than leaving the Pivot pointing at a missing key. */}
            <Pivot
                selectedKey={isContent && (selectedTab === 'header' || selectedTab === 'footer') ? 'basic' : selectedTab}
                onLinkClick={(item) => setSelectedTab((item?.props.itemKey as SectionTabKey) ?? 'basic')}
            >
                <PivotItem headerText='Basic' itemKey='basic'>
                    <DynamicSetting id={`storefront.sectionField.${props.section.id}.sectionType`}>
                        <Dropdown
                            label='Section Type'
                            selectedKey={props.section.type}
                            onChange={(_, option) => {
                                const newType = option?.key as string;
                                // Switching to Rich Text seeds the column cells (migrating any legacy
                                // single-markdown contentConfig into the first cell) so the section is
                                // immediately editable in the preview. contentConfig itself is left
                                // untouched, so an already-configured CTA button survives the switch.
                                if (newType === 'richText' && !(props.section.headerCells?.length)) {
                                    const legacy = parseRichTextConfig(props.section.contentConfig).markdown;
                                    const seeded: IStorefrontSectionCell[] = [{ type: 'text', text: legacy ?? '' }];
                                    setTempHeaderCells(seeded);
                                    props.onChange({ ...props.section, type: newType, headerCells: seeded });
                                    return;
                                }
                                // A divider is a standalone rule — default its header hidden so it reads
                                // as a divider, not a titled section (only when hideTitle isn't already set).
                                if (newType === 'divider' && props.section.hideTitle === undefined) {
                                    props.onChange({ ...props.section, type: newType, hideTitle: true });
                                    return;
                                }
                                props.onChange({ ...props.section, type: newType });
                            }}
                            options={typeDropdownOptions}
                        />
                    </DynamicSetting>
                    <DynamicSetting id={`storefront.sectionField.${props.section.id}.width`}>
                        <Dropdown
                            label='Width'
                            selectedKey={props.section.width ?? 'full'}
                            onChange={(_, option) => {
                                // 'full' is the default — store undefined for it so unchanged sections stay clean
                                // (the server also treats null as full).
                                const key = option?.key as NonNullable<IStorefrontSection['width']>;
                                props.onChange({ ...props.section, width: key === 'full' ? undefined : key });
                            }}
                            options={widthOptions}
                        />
                    </DynamicSetting>
                    <ConditionalSetting when={isFractionalWidth(props.section.width)} idPrefix={`storefront.sectionField.${props.section.id}.verticalAlignment`}>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.verticalAlignment`}>
                            <Dropdown
                                label='Vertical Alignment'
                                selectedKey={props.section.verticalAlign ?? 'stretch'}
                                onChange={(_, option) => {
                                    // 'stretch' is the default — store undefined so unchanged sections stay clean.
                                    const key = option?.key as 'stretch' | 'top' | 'center' | 'bottom';
                                    props.onChange({ ...props.section, verticalAlign: key === 'stretch' ? undefined : key });
                                }}
                                options={verticalAlignOptions}
                            />
                        </DynamicSetting>
                        <div style={{ fontSize: 12, color: '#605e5c', paddingBottom: '0.5em' }}>
                            How this section sits vertically next to the other sections in its row.
                        </div>
                    </ConditionalSetting>
                    <DynamicSetting id={`storefront.sectionField.${props.section.id}.hideHeader`}>
                        <Toggle
                            label='Hide header on storefront'
                            inlineLabel
                            onText='Hidden'
                            offText='Shown'
                            checked={props.section.hideTitle ?? false}
                            onChange={(_, checked) => props.onChange({ ...props.section, hideTitle: checked ?? false })}
                        />
                    </DynamicSetting>
                    <div style={{ fontSize: 12, color: '#605e5c', paddingBottom: '0.5em' }}>
                        Hiding the header also removes the shopper's collapse control for this section. The
                        title is still used as the section's name in admin pickers.
                    </div>
                    {/* Only meaningful with a visible header — a hidden header has nothing to click and
                        no room for the control, so the section is already permanently expanded. */}
                    <ConditionalSetting when={!props.section.hideTitle} idPrefix={`storefront.sectionField.${props.section.id}.collapsible`}>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.collapsible`}>
                            <Toggle
                                label='Shoppers can collapse this section'
                                inlineLabel
                                onText='Collapsible'
                                offText='Always open'
                                checked={props.section.collapsible !== false}
                                onChange={(_, checked) => props.onChange({ ...props.section, collapsible: checked ?? true })}
                            />
                        </DynamicSetting>
                        <div style={{ fontSize: 12, color: '#605e5c', paddingBottom: '0.5em' }}>
                            When off, the header shows no collapse control and clicking it does nothing, so the
                            section always renders open. Style the control for the whole page under Home Page
                            Style &rarr; Raw JSON (<code>sectionToggle</code>).
                        </div>
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={!isContent}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.maxProducts`,
                            `storefront.sectionField.${props.section.id}.sideScroll`,
                        ]}
                    >
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.maxProducts`}>
                            <NumberField
                                label='Max Number of Products to Show'
                                toolTip='The maximum number of products to show on the section. If more products match the criteria, only a subset will be shown. This value cannot be set above 25'
                                value={props.section.maxEntries ?? 0}
                                onChange={(value) => props.onChange({ ...props.section, maxEntries: value })}
                            />
                        </DynamicSetting>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.sideScroll`}>
                            <Toggle
                                label='Side-scroll products'
                                inlineLabel
                                onText='On'
                                offText='Off'
                                checked={props.section.scrollProducts ?? false}
                                onChange={(_, checked) => props.onChange({ ...props.section, scrollProducts: checked ?? false })}
                            />
                        </DynamicSetting>
                    </ConditionalSetting>
                    <ConditionalSetting when={props.section.type === 'video'} idPrefix={`storefront.sectionField.${props.section.id}.videoUrl`}>
                        <VideoContentForm section={props.section} onChange={props.onChange} />
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={props.section.type === 'richText'}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.columns`,
                            `storefront.sectionField.${props.section.id}.buttonLabel`,
                            `storefront.sectionField.${props.section.id}.buttonUrl`,
                            `storefront.sectionField.${props.section.id}.buttonPlacement`,
                            `storefront.sectionField.${props.section.id}.buttonAlignment`,
                            `storefront.sectionStyle.${props.section.id}.button`,
                        ]}
                    >
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.columns`}>
                            <ChoiceGroup
                                label='Columns'
                                selectedKey={String(Math.min(Math.max(tempHeaderCells.length, 1), 3))}
                                onChange={changeRichTextColumnCount}
                                options={[
                                    { key: '1', text: 'One Column', iconProps: { iconName: 'SingleColumn' } },
                                    { key: '2', text: 'Two Columns', iconProps: { iconName: 'DoubleColumn' } },
                                    { key: '3', text: 'Three Columns', iconProps: { iconName: 'TripleColumn' } },
                                ]}
                            />
                        </DynamicSetting>
                        <Stack tokens={{ childrenGap: 8, padding: '1em 0' }}>
                            {tempHeaderCells.map((cell, index) => (
                                <Stack key={index} horizontal tokens={{ childrenGap: 4 }} verticalAlign='end'>
                                    <StackItem grow>
                                        <Dropdown
                                            label={`Column ${index + 1} Type`}
                                            selectedKey={cell.type}
                                            onChange={(_, option) => {
                                                const updatedCells = [...tempHeaderCells];
                                                updatedCells[index] = { ...cell, type: option?.key as string };
                                                setTempHeaderCells(updatedCells);
                                            }}
                                            options={cellTypeOptions}
                                        />
                                    </StackItem>
                                    {tempHeaderCells.length >= 2 && <>
                                        <IconButton iconProps={{ iconName: 'ChevronLeftSmall' }} title='Move column left' disabled={index === 0} onClick={() => moveRichTextCell(index, -1)} />
                                        <IconButton iconProps={{ iconName: 'ChevronRightSmall' }} title='Move column right' disabled={index === tempHeaderCells.length - 1} onClick={() => moveRichTextCell(index, 1)} />
                                    </>}
                                </Stack>
                            ))}
                            <span style={{ fontSize: 12, color: '#605e5c' }}>
                                Click into a column in the preview to edit its content.
                            </span>
                        </Stack>
                        {/* The optional CTA button (contentConfig.button) — content stays in the cells above. */}
                        <RichTextContentForm section={props.section} onChange={props.onChange} />
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={props.section.type === 'banner'}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.bannerHeight`,
                            `storefront.sectionField.${props.section.id}.bannerImageFit`,
                            `storefront.sectionField.${props.section.id}.bannerFullBleed`,
                            `storefront.sectionField.${props.section.id}.bannerBackground`,
                        ]}
                    >
                        <BannerContentForm section={props.section} onChange={props.onChange} />
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={props.section.type === 'divider'}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.dividerLineStyle`,
                            `storefront.sectionField.${props.section.id}.dividerWidth`,
                            `storefront.sectionField.${props.section.id}.dividerSpacing`,
                            `storefront.sectionField.${props.section.id}.dividerThickness`,
                            `storefront.sectionField.${props.section.id}.dividerColor`,
                        ]}
                    >
                        <DividerContentForm section={props.section} onChange={props.onChange} />
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={props.section.type === 'singleProduct'}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.blurb`,
                            `storefront.sectionField.${props.section.id}.ctaButtonText`,
                        ]}
                    >
                        <SingleProductContentForm section={props.section} onChange={props.onChange} storefrontId={props.storefrontId} />
                    </ConditionalSetting>
                    {props.section.type === 'selectedProducts' && <SelectedProductsForm section={props.section} onChange={props.onChange} storefrontId={props.storefrontId} />}
                    <ConditionalSetting
                        when={props.section.type === 'categoriesSegments'}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.manufacturers`,
                            `storefront.sectionField.${props.section.id}.segment`,
                            `storefront.sectionField.${props.section.id}.categories`,
                            `storefront.sectionField.${props.section.id}.subCategories`,
                        ]}
                    >
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.manufacturers`}>
                            <Dropdown
                                label='Manufacturers'
                                multiSelect
                                selectedKeys={props.section.manufacturers ?? []}
                                onChange={(_, option) => {
                                    const selectedKeys = option?.selected
                                        ? [...(props.section.manufacturers ?? []), option.key as string]
                                        : (props.section.manufacturers ?? []).filter(k => k !== option?.key);
                                    props.onChange({ ...props.section, manufacturers: selectedKeys });
                                }}
                                options={manufacturers.data?.items.map(m => ({ key: m, text: m })) ?? []}
                                placeholder='All manufacturers'
                            />
                        </DynamicSetting>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.segment`}>
                            <Dropdown
                                label='Product Segment'
                                multiSelect
                                selectedKeys={props.section.segments ?? []}
                                onChange={(_, option) => {
                                    const selectedKeys = option?.selected ? [...(props.section.segments ?? []), option.key as string] : (props.section.segments ?? []).filter(k => k !== option?.key);
                                    props.onChange({ ...props.section, segments: selectedKeys, categories: [], subCategories: [] });
                                }}
                                options={segments.data?.items.map(s => ({ key: s.label ?? '', text: s.label ?? s.identifier ?? s.id ?? '' })) ?? []}
                            />
                        </DynamicSetting>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.categories`}>
                            <Dropdown
                                label='Product Categories'
                                multiSelect
                                selectedKeys={props.section.categories ?? []}
                                onChange={(_, option) => {
                                    const selectedKeys = option?.selected ? [...(props.section.categories ?? []), option.key as string] : (props.section.categories ?? []).filter(k => k !== option?.key);
                                    props.onChange({ ...props.section, categories: selectedKeys, subCategories: [] });
                                }}
                                options={categoryOptions ?? []}
                                disabled={categoryOptions == null || categoryOptions.length === 0 || loadingCategories}
                                placeholder={loadingCategories ? 'Loading' : undefined}
                            />
                        </DynamicSetting>
                        <ConditionalSetting when={(props.section.categories ?? []).length > 0} idPrefix={`storefront.sectionField.${props.section.id}.subCategories`}>
                            <DynamicSetting id={`storefront.sectionField.${props.section.id}.subCategories`}>
                                <Dropdown
                                    label='Sub Categories'
                                    multiSelect
                                    selectedKeys={props.section.subCategories ?? []}
                                    onChange={(_, option) => {
                                        const selectedKeys = option?.selected
                                            ? [...(props.section.subCategories ?? []), option.key as string]
                                            : (props.section.subCategories ?? []).filter(k => k !== option?.key);
                                        props.onChange({ ...props.section, subCategories: selectedKeys });
                                    }}
                                    options={subCategories.data?.items.map(s => ({ key: s, text: s })) ?? []}
                                    placeholder='All sub categories'
                                />
                            </DynamicSetting>
                        </ConditionalSetting>
                        {/* Tags work standalone — e.g. "AI System" across every segment — or narrow the
                            segment/category/manufacturer selections above. Global-tag autocomplete, same
                            as the product panel's Tags tab. */}
                        <Label>Tags</Label>
                        <ProductTagsAutoSuggest
                            editable
                            globalOnly
                            inputWidth='100%'
                            placeholder='Search Tags / Add Tag (Press Enter)'
                            onCreate={(tag) => {
                                const existing = props.section.tags ?? [];
                                if (existing.includes(tag)) return;
                                props.onChange({ ...props.section, tags: [...existing, tag] });
                            }}
                        />
                        {(props.section.tags ?? []).map((tag) => (
                            <SearchFilter key={tag} color='#9BD3AB' protected={false}
                                onDelete={() => props.onChange({ ...props.section, tags: (props.section.tags ?? []).filter(t => t !== tag) })}>
                                {tag}
                            </SearchFilter>
                        ))}
                        <div style={{ fontSize: 12, color: '#605e5c', paddingTop: 4 }}>
                            When set, this section only shows products carrying any of these tags — with or without
                            segment/category filters (requires Product Tags enabled on the storefront).
                        </div>
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={isSelectedPromos}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.promotions`,
                            `storefront.sectionField.${props.section.id}.distributorPromotions`,
                        ]}
                    >
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.promotions`}>
                            <Dropdown
                                label='Promotions'
                                multiSelect
                                placeholder={promotions.isFetching ? 'Loading…' : 'Choose promotions'}
                                selectedKeys={selectedPromotionIds}
                                options={(promotions.data ?? [])
                                    .flatMap(p => p.id
                                        ? [{
                                            key: p.id,
                                            text: p.productCount ? `${p.name ?? 'Untitled'} (${p.productCount})` : (p.name ?? 'Untitled'),
                                        }]
                                        : [])}
                                onChange={(_, option) => {
                                    if (!option) return;
                                    const key = option.key as string;
                                    updatePromo({
                                        promotionIds: option.selected
                                            ? [...selectedPromotionIds, key]
                                            : selectedPromotionIds.filter(id => id !== key),
                                    });
                                }}
                            />
                        </DynamicSetting>
                        <div style={{ fontSize: 12, color: '#605e5c', paddingTop: 4 }}>
                            Only promotions running now and scoped to this storefront are listed. Choosing one
                            never overrides its schedule — the row still empties itself when the promotion ends.
                        </div>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.distributorPromotions`}>
                            <Dropdown
                                label='Distributor Promotions'
                                multiSelect
                                placeholder={promoVendorList.isFetching ? 'Loading…' : 'Choose distributors'}
                                selectedKeys={selectedPromoVendors}
                                options={promoVendorList?.data?.items?.map((v: string) => ({ key: v, text: v })) ?? []}
                                onChange={(_, option) => {
                                    if (!option) return;
                                    const key = option.key as string;
                                    updatePromo({
                                        vendors: option.selected
                                            ? [...selectedPromoVendors, key]
                                            : selectedPromoVendors.filter(v => v !== key),
                                    });
                                }}
                            />
                        </DynamicSetting>
                        <div style={{ fontSize: 12, color: '#605e5c', paddingTop: 4 }}>
                            Shows products a distributor is currently discounting. Needs vendor promotions enabled
                            on this storefront&rsquo;s product settings, or nothing from them will appear.
                        </div>
                        {selectedPromotionIds.length === 0 && selectedPromoVendors.length === 0 && (
                            <div style={{ fontSize: 12, color: '#a4262c', paddingTop: 4 }}>
                                Nothing selected — this section will not appear on the storefront.
                            </div>
                        )}
                    </ConditionalSetting>
                    <ConditionalSetting
                        when={props.section.type === 'interest'}
                        idPrefix={[
                            `storefront.sectionField.${props.section.id}.recommendationMaxProducts`,
                            `storefront.sectionField.${props.section.id}.recencyHalfLife`,
                            `storefront.sectionField.${props.section.id}.excludePurchased`,
                        ]}
                    >
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.recommendationMaxProducts`}>
                            <NumberField
                                label='Max Products to Show'
                                toolTip='Maximum number of recommended products to show in the row. Leave blank for the default (12).'
                                placeholder='12'
                                value={interestCfg.maxItems}
                                onChange={(value) => updateInterest({ maxItems: value })}
                            />
                        </DynamicSetting>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.recencyHalfLife`}>
                            <NumberField
                                label='Recency Half-life (days)'
                                toolTip='How quickly older activity loses weight. At this many days, an activity counts half as much. Leave blank for the default (30).'
                                placeholder='30'
                                decimal
                                value={interestCfg.recencyHalfLifeDays}
                                onChange={(value) => updateInterest({ recencyHalfLifeDays: value })}
                            />
                        </DynamicSetting>
                        <DynamicSetting id={`storefront.sectionField.${props.section.id}.excludePurchased`}>
                            <Toggle
                                label='Exclude items already purchased'
                                inlineLabel
                                onText='On'
                                offText='Off'
                                checked={interestCfg.excludeAlreadyPurchased ?? false}
                                onChange={(_, checked) => updateInterest({ excludeAlreadyPurchased: checked ?? false })}
                            />
                        </DynamicSetting>
                        <Label style={{ paddingTop: '0.5em' }}>Signals</Label>
                        <div style={{ fontSize: 12, color: '#605e5c', paddingBottom: 4 }}>
                            Choose which shopper activity feeds this row. It starts empty — add at least one
                            signal or nothing shows. Weight is optional; leave blank to use the default.
                        </div>
                        {interestSignals.length === 0 && (
                            <div style={{ fontSize: 12, fontStyle: 'italic', color: '#a19f9d', padding: '0.25em 0' }}>
                                No signals yet — add one below to populate this row.
                            </div>
                        )}
                        {interestSignals.map((rule) => {
                            const meta = INTEREST_SOURCES.find(s => s.key === rule.source);
                            return (
                                <Stack key={rule.source} horizontal verticalAlign='end' tokens={{ childrenGap: '0.5em' }} styles={{ root: { paddingBottom: '0.25em' } }}>
                                    <StackItem grow>
                                        <Label styles={{ root: { padding: 0 } }} title={meta?.hint}>{meta?.label ?? rule.source}</Label>
                                    </StackItem>
                                    <StackItem styles={{ root: { width: 90 } }}>
                                        <NumberField
                                            label='Weight'
                                            toolTip={`${meta?.hint ?? ''} Default ${meta?.defaultWeight ?? 1} when blank.`}
                                            placeholder={`${meta?.defaultWeight ?? 1}`}
                                            decimal
                                            value={rule.weight}
                                            onChange={(value) => setInterestSignalWeight(rule.source, value)}
                                        />
                                    </StackItem>
                                    <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        title='Remove signal'
                                        ariaLabel='Remove signal'
                                        onClick={() => removeInterestSignal(rule.source)}
                                    />
                                </Stack>
                            );
                        })}
                        {availableInterestSources.length > 0 && (
                            <Dropdown
                                label='Add a signal'
                                placeholder='Select a signal to add'
                                selectedKey={null}
                                onChange={(_, option) => option && addInterestSignal(option.key as InterestSignalSource)}
                                options={availableInterestSources.map(s => ({ key: s.key, text: s.label, title: s.hint }))}
                            />
                        )}
                        <div style={{ fontSize: 12, color: '#605e5c', paddingTop: 8 }}>
                            Personalized per signed-in shopper. Guests don&rsquo;t see this row. New storefronts
                            fill in over time as shoppers browse.
                        </div>
                    </ConditionalSetting>
                </PivotItem>
                {!isContent && <PivotItem headerText='Header' itemKey='header'>
                    <ChoiceGroup
                        label='Layout'
                        selectedKey={headerCellCount?.toString() ?? '0'}
                        onChange={changeHeaderCellCount}
                        options={[
                            { key: '0', text: 'None', iconProps: { iconName: 'Clear' } },
                            { key: '1', text: 'One Column', iconProps: { iconName: 'SingleColumn' } },
                            { key: '2', text: 'Two Columns', iconProps: { iconName: 'DoubleColumn' } },
                        ]}
                        />
                        <Stack horizontal tokens={{childrenGap: '1em', padding: '1em 0'}}>
                            <StackItem grow>
                                {tempHeaderCells.map((cell, index) => (
                                    <Dropdown
                                        label={`Cell ${index + 1} Type`}
                                        selectedKey={cell.type}
                                        onChange={(_, option) => {
                                            const newType = option?.key as string;
                                            const updatedCell = { ...cell, type: newType };
                                            const updatedCells = [...tempHeaderCells];
                                            updatedCells[index] = updatedCell;
                                            setTempHeaderCells(updatedCells);
                                        }}
                                        options={cellTypeOptions}
                                    />
                                ))}
                            </StackItem>
                            {tempHeaderCells.length >= 2 && <StackItem align='center'>
                                <IconButton
                                    iconProps={{ iconName: 'SwitcherStartEnd' }}
                                    onClick={() => {
                                        const updatedCells = [...tempHeaderCells];
                                        const temp = updatedCells[0];
                                        updatedCells[0] = updatedCells[1];
                                        updatedCells[1] = temp;
                                        setTempHeaderCells(updatedCells);
                                    }}
                                    />
                            </StackItem>}
                        </Stack>

                </PivotItem>}
                {!isContent && <PivotItem headerText='Footer' itemKey='footer'>
                    <ChoiceGroup
                        label='Layout'
                        selectedKey={footerCellCount?.toString() ?? '0'}
                        onChange={(ev, option) => {
                            const newCount = Number(option?.key);
                            setFooterCellCount(newCount);
                            const newCells: IStorefrontSectionCell[] = [];
                            for(let i = 0; i < newCount; i++) {
                                if (props.section.footerCells && props.section.footerCells[i]) {
                                    newCells.push(props.section.footerCells[i]);
                                } else {
                                    newCells.push({ type: 'text', text: '' });
                                }
                            }
                            setTempFooterCells(newCells);
                        }}
                        options={[
                            { key: '0', text: 'None', iconProps: { iconName: 'Clear' } },
                            { key: '1', text: 'One Column', iconProps: { iconName: 'SingleColumn' } },
                            { key: '2', text: 'Two Columns', iconProps: { iconName: 'DoubleColumn' } },
                        ]}
                        />
                        {tempFooterCells.map((cell, index) => (
                            <Dropdown
                                label={`Cell ${index + 1} Type`}
                                selectedKey={cell.type}
                                onChange={(_, option) => {
                                    const newType = option?.key as string;
                                    const updatedCell = { ...cell, type: newType };
                                    const updatedCells = [...tempFooterCells];
                                    updatedCells[index] = updatedCell;
                                    setTempFooterCells(updatedCells);
                                }}
                                options={cellTypeOptions}
                            />
                        ))}
                </PivotItem>}
                <PivotItem headerText='Appearance' itemKey='appearance'>
                    {/* 460px panel -> a roomier control column than the 340px Styles panel's 180. */}
                    <StyleRowsWidthProvider width={220}>
                    {/* A FLAT surface, not a stack of accordion pods: each group is a lightweight
                        header row (StyleGroupHeader — same visual language as the Styles panel's
                        part groups) with its style picker ALWAYS visible underneath. Picking a named
                        style is the primary path now, so nothing has to be unfolded to reach it;
                        only the raw CSS overrides fold away, behind StyleOverridesGroup's demoted
                        "Advanced overrides (N)" chevron. The group headers are deliberately NOT
                        collapsible — with the overrides folded the tab is short enough, and footer /
                        product groups don't render for content sections at all. */}
                    <DynamicSetting id={`storefront.sectionStyle.${props.section.id}.section`}>
                        <StyleGroupHeader
                            label='Section'
                            iconName={STYLE_PART_ICONS.band}
                            tooltip='The section container: background, border, and default text styling.'
                        />
                    </DynamicSetting>
                    <StyleOverridesGroup
                        pickerLabel='Style'
                        rowLayout
                        value={props.section.styleProps}
                        onChange={(patch) => props.onChange({ ...props.section, styleProps: { ...props.section.styleProps, ...patch } })}
                        target={styleTargetForKey('styleProps')}
                        alwaysVisible={fullWidthBackgroundToggle}
                    >
                        <CssCopyPasteButtons
                            onPaste={(css) => {
                                props.onChange({ ...props.section, styleProps: css });
                            }}
                            onClear={() => props.onChange({ ...props.section, styleProps: clearedStyleProps(props.section.styleProps) })}
                            css={props.section.styleProps}
                        />
                        <SectionCssRows
                            css={props.section.styleProps}
                            settingKeyPrefix={`storefront.sectionStyle.${props.section.id}.section`}
                            onChange={(property, value) => {
                                const updatedCss = { ...props.section.styleProps, [property]: value };
                                props.onChange({ ...props.section, styleProps: updatedCss });
                            }}
                        />
                    </StyleOverridesGroup>

                    {/* Header group: the title band the storefront actually renders (titleStyleProps). */}
                    <DynamicSetting id={`storefront.sectionStyle.${props.section.id}.header`}>
                        <StyleGroupHeader
                            label='Header'
                            iconName={STYLE_PART_ICONS.header}
                            tooltip='The title bar above the section content. Font settings here style the section title.'
                        />
                    </DynamicSetting>
                    <StyleOverridesGroup
                        pickerLabel='Style'
                        rowLayout
                        value={props.section.titleStyleProps}
                        onChange={(patch) => props.onChange({ ...props.section, titleStyleProps: { ...props.section.titleStyleProps, ...patch } })}
                        target={styleTargetForKey('titleStyleProps')}
                    >
                        <CssCopyPasteButtons
                            onPaste={(css) => {
                                props.onChange({ ...props.section, titleStyleProps: css });
                            }}
                            onClear={() => props.onChange({ ...props.section, titleStyleProps: clearedStyleProps(props.section.titleStyleProps) })}
                            css={props.section.titleStyleProps}
                        />
                        <SectionCssRows
                            css={props.section.titleStyleProps}
                            settingKeyPrefix={`storefront.sectionStyle.${props.section.id}.header`}
                            onChange={(property, value) => {
                                const updatedCss = { ...props.section.titleStyleProps, [property]: value };
                                props.onChange({ ...props.section, titleStyleProps: updatedCss });
                            }}
                        />
                    </StyleOverridesGroup>

                    {/* Footer group: the row of footer cells under the section content (footerStyleProps). */}
                    <ConditionalSetting when={!isContent} idPrefix={`storefront.sectionStyle.${props.section.id}.footer`}>
                        <DynamicSetting id={`storefront.sectionStyle.${props.section.id}.footer`}>
                            <StyleGroupHeader
                                label='Footer'
                                iconName={STYLE_PART_ICONS.footer}
                                tooltip='The row of footer cells below the section content.'
                            />
                        </DynamicSetting>
                        <StyleOverridesGroup
                            pickerLabel='Style'
                            rowLayout
                            value={props.section.footerStyleProps}
                            onChange={(patch) => props.onChange({ ...props.section, footerStyleProps: { ...props.section.footerStyleProps, ...patch } })}
                            target={styleTargetForKey('footerStyleProps')}
                        >
                            <CssCopyPasteButtons
                                onPaste={(css) => {
                                    props.onChange({ ...props.section, footerStyleProps: css });
                                }}
                                onClear={() => props.onChange({ ...props.section, footerStyleProps: clearedStyleProps(props.section.footerStyleProps) })}
                                css={props.section.footerStyleProps}
                            />
                            <SectionCssRows
                                css={props.section.footerStyleProps}
                                settingKeyPrefix={`storefront.sectionStyle.${props.section.id}.footer`}
                                onChange={(property, value) => {
                                    const updatedCss = { ...props.section.footerStyleProps, [property]: value };
                                    props.onChange({ ...props.section, footerStyleProps: updatedCss });
                                }}
                            />
                        </StyleOverridesGroup>
                    </ConditionalSetting>

                    {/* Product group: the cards rendered inside the section (productStyleProps).
                        These reference a CARD style, not a section style. Wrapped in one element so
                        the card popover's "Advanced overrides" deep link has something to scroll to —
                        the header and its group have to arrive together. */}
                    <ConditionalSetting when={!isContent} idPrefix={`storefront.sectionStyle.${props.section.id}.productCards`}>
                        <div ref={productGroupRef}>
                            <DynamicSetting id={`storefront.sectionStyle.${props.section.id}.productCards`}>
                                <StyleGroupHeader
                                    label='Product Cards'
                                    iconName={STYLE_PART_ICONS.product}
                                    tooltip='Styling for the product cards inside this section. Pick a saved Product Card style, or set CSS directly.'
                                />
                            </DynamicSetting>
                            <StyleOverridesGroup
                                pickerLabel='Style'
                                rowLayout
                                value={props.section.productStyleProps}
                                onChange={(patch) => props.onChange({ ...props.section, productStyleProps: { ...props.section.productStyleProps, ...patch } })}
                                target={styleTargetForKey('productStyleProps')}
                                // Arriving from the card popover, the merchant already chose "advanced" —
                                // landing on a collapsed fold would hide the very controls they asked for.
                                defaultExpanded={focusProductStyle}
                            >
                                <SectionCssRows
                                    css={props.section.productStyleProps}
                                    settingKeyPrefix={`storefront.sectionStyle.${props.section.id}.productCards`}
                                    onChange={(property, value) => {
                                        const updatedCss = { ...props.section.productStyleProps, [property]: value };
                                        props.onChange({ ...props.section, productStyleProps: updatedCss });
                                    }}
                                />
                            </StyleOverridesGroup>
                        </div>
                    </ConditionalSetting>
                    {/* The page-level layers a section may override (AC-1165): the product card and its
                        name, price and image, the add-to-cart button, the side-scroll chrome, and the
                        knocked-out block behind the section title. Each of these used to live only on the
                        page, one set shared by every section, which is why two sections could not differ
                        below the band.

                        Rendered from SECTION_OVERRIDE_PARTS — the same table the capture and both
                        resolvers use — so this list cannot drift from what actually resolves. A content
                        section shows only the title block: it has no product cards to style. */}
                    {SECTION_OVERRIDE_KEYS
                        .filter((key) => (isContent ? key === 'sectionTitleText' : true))
                        .map((key) => {
                            const part = SECTION_OVERRIDE_PARTS[key];
                            const label = STYLE_PART_LABELS[part] ?? key;
                            const patchOverride = (patch: { [prop: string]: any }) => props.onChange({
                                ...props.section,
                                styleOverrides: {
                                    ...props.section.styleOverrides,
                                    [key]: { ...props.section.styleOverrides?.[key], ...patch },
                                },
                            });
                            return (
                                <React.Fragment key={key}>
                                    <StyleGroupHeader
                                        label={label}
                                        iconName={STYLE_PART_ICONS[part] ?? STYLE_PART_ICONS.product}
                                        tooltip={`Overrides the page-wide ${label.toLowerCase()} styling for this section only. Left alone, the section follows the page.`}
                                    />
                                    <StyleOverridesGroup
                                        pickerLabel='Style'
                                        rowLayout
                                        value={props.section.styleOverrides?.[key]}
                                        onChange={patchOverride}
                                        target={{ elementType: 'section', part }}
                                    >
                                        <SectionCssRows
                                            css={props.section.styleOverrides?.[key]}
                                            settingKeyPrefix={`storefront.sectionStyle.${props.section.id}.override.${key}`}
                                            onChange={(property, value) => patchOverride({ [property]: value })}
                                        />
                                    </StyleOverridesGroup>
                                </React.Fragment>
                            );
                        })}
                    </StyleRowsWidthProvider>
                </PivotItem>
                <PivotItem headerText='Advanced' itemKey='advanced'>
                    <RawJsonEditor
                        label='Section JSON'
                        liveObject={props.section}
                        onApply={(json) => props.onChange(JSON.parse(json) as IStorefrontSection)}
                        keysHint='Edits the entire section object. Keep id, type, and storefront intact — removing them can break saving. Style lives in styleProps / headerStyleProps / footerStyleProps; content in title, headerCells, footerCells, and the product query fields. Changes apply to the preview while the JSON is valid.'
                    >
                        <div style={{ fontSize: 12, color: '#605e5c', padding: '0.5em 0' }}>
                            Toggle <strong>Raw JSON</strong> to edit this section's full configuration — type, product query, cells, and style — as JSON.
                        </div>
                    </RawJsonEditor>
                </PivotItem>
            </Pivot>
        </Panel>
    </div>
    </StorefrontCardChromeProvider>;
}

interface IStorefrontSectionCellEditorProps {
    cell: IStorefrontSectionCell;
    onChange: (cell: IStorefrontSectionCell) => void;
    editMode?: boolean;
}

const StorefrontSectionCellEditor: React.FC<IStorefrontSectionCellEditorProps> = (props) => {
    // The cell's own box never carries the reserved `$title` key, and the title renders with the
    // scale its own text asks for, exactly as the storefront draws it.
    const styleProps = props.cell.styleProps as { [key: string]: unknown } | undefined;
    const bodyStyle = cellBodyStyle(styleProps);
    // A CARD is media plus copy of its own. A card leads with its media (image, then title, then
    // text): that is what a real feature card does, and with the icon on top the icons in a row line
    // up instead of each starting wherever its title happened to wrap to. A cell with no copy has
    // nothing to order. Mirrors renderSectionCells in the storefront's SectionCells.tsx.
    const isCard = !!(props.cell.title || props.cell.text);
    // Stacking as a column only makes each card's parts start in the same place. It does NOT force
    // the cells in a row to equal heights. Applied on the media cell types only, which is where the
    // storefront applies it (a text-only cell has nothing to reorder and stays in block flow).
    const cardStack: React.CSSProperties = isCard ? { display: 'flex', flexDirection: 'column' } : {};
    const title = props.cell.title
        ? <div className='storefront-section-cell-title' style={cellTitleStyle(styleProps, props.cell.title)}>{props.cell.title}</div>
        : null;
    if (props.cell.type === 'image') {
        // Edit mode: the image itself is the editor (click to upload/replace — same ImageEditor the
        // legacy section editor uses). Preview: the plain image.
        return <div className='storefront-section-cell' style={{ ...cardStack, ...bodyStyle }}>
            {props.editMode
                ? <ImageEditor
                    value={props.cell.sourceUrl}
                    onChange={(url) => props.onChange({ ...props.cell, sourceUrl: url })}
                    width={'100%'}
                    height={'100%'}
                />
                : <Image src={props.cell.sourceUrl} style={cellMediaStyle(bodyStyle, isCard)} />}
            {title}
        </div>
    }
    if (props.cell.type === 'video') {
        const videoId = youTubeId(props.cell.sourceUrl) ?? (/^[\w-]{11}$/.test(props.cell.sourceUrl ?? '') ? props.cell.sourceUrl : null);
        return <div className='storefront-section-cell' style={{ ...cardStack, ...bodyStyle }}>
            {props.editMode && <TextField
                label='YouTube Video Id or URL'
                value={props.cell.sourceUrl ?? ''}
                onChange={(_, v) => props.onChange({ ...props.cell, sourceUrl: v })}
            />}
            {videoId && (
                <iframe
                    width={(bodyStyle.width as string | number | undefined) ?? '100%'}
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title='YouTube video player'
                    frameBorder='0'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                    allowFullScreen
                    style={{ aspectRatio: '16 / 9', border: 0 }}
                ></iframe>
            )}
            {title}
        </div>
    }
    if (props.editMode) {
        return <div className='storefront-section-cell' style={bodyStyle}>
            {title}
            <RichTextField
                value={props.cell.text ?? ''}
                onChange={(text) => props.onChange({ ...props.cell, text })}
                placeholder='Cell Text'
                style={{width: '100%', height: '100%'}}
                previewStyle={{width: '100%', height: '100%', backgroundColor: 'transparent'}}
            />
        </div>
    }
    return <div className='storefront-section-cell' style={bodyStyle}>
        {title}
        <MarkdownView markdown={props.cell.text ?? ''} />
    </div>
}

export default StorefrontSectionEditor;
